import { ApolloError } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import {
  type SaveActivationFlowConfigBundleInput,
  useSaveActivationFlowConfigBundle,
} from 'src/graphql';
import { type PlansTabFormValues } from 'src/pages/Settings/memberships/components/plans-tab/types';
import {
  type ConfigFormSaveRequest,
  type ConfigFormSubmitState,
  type ConfigSurfaceController,
  EMPTY_CONFIG_FORM_SUBMIT_STATE,
} from 'src/pages/Settings/memberships/types';
import { hasAtLeastOneEnabledProduct } from 'src/pages/Settings/memberships/utils/has-at-least-one-enabled-product';
import { parseMembershipsConfigError } from 'src/pages/Settings/memberships/utils/parse-memberships-config-error';
import { serializeMembershipsConfigPayload } from 'src/pages/Settings/memberships/utils/serialize-config-payload';
import { useGlobalStore } from 'src/store';
import { useConfirmationModal } from 'src/utils/hooks';
import { useNotifications } from 'src/utils/hooks/use-notifications';

const SAVE_FAILED_MESSAGE = 'Unable to save membership configuration. Please try again.';

interface UseMembershipsConfigSurfaceParams {
  partnerId: number | null;
  selectedSegmentId: string | null;
  activationFlowConfigId: string | null;
  formKey: string;
  canEdit: boolean;
  onSaved?: () => void | Promise<void>;
}

interface MembershipsConfigSurface extends ConfigSurfaceController<PlansTabFormValues> {
  readOnly: boolean;
  liveFormValues: PlansTabFormValues | null;
  onFormValuesChange: (values: PlansTabFormValues) => void;
}

export const useMembershipsConfigSurface = ({
  partnerId,
  selectedSegmentId,
  activationFlowConfigId,
  formKey,
  canEdit,
  onSaved,
}: UseMembershipsConfigSurfaceParams): MembershipsConfigSurface => {
  const { alert } = useConfirmationModal();
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { saveActivationFlowConfigBundle, loading: saveConfigLoading } =
    useSaveActivationFlowConfigBundle();

  const [requestSaveValues, setRequestSaveValues] =
    useState<ConfigFormSaveRequest<PlansTabFormValues> | null>(null);
  const [submitState, setSubmitState] = useState<ConfigFormSubmitState>(
    EMPTY_CONFIG_FORM_SUBMIT_STATE,
  );
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [liveFormValues, setLiveFormValues] = useState<PlansTabFormValues | null>(null);

  const readOnly = !canEdit;
  const hasPartner = !isNullish(partnerId);
  const hasUnsavedChanges = Boolean(requestSaveValues) && isFormDirty;
  const isSaving = submitState.isSubmitting || saveConfigLoading;

  // Reset live form values when segment or form key changes so preview reflects the new config.
  useEffect(() => {
    setLiveFormValues(null);
  }, [formKey, selectedSegmentId]);

  const handleSave = useCallback(async () => {
    if (readOnly) {
      return;
    }

    if (!selectedSegmentId || !hasPartner) {
      await alert({
        title: 'Select a segment first',
        body: 'Pick a partner and client segment before saving.',
        acknowledgeLabel: 'Got it',
      });
      return;
    }

    if (!requestSaveValues) {
      return;
    }

    if (
      isNullish(partnerId) ||
      isNullish(selectedPartnerId) ||
      partnerId !== selectedPartnerId
    ) {
      showErrorAlert('Selected partner changed. Please reselect the partner and try again.');
      return;
    }

    const savePayload = await requestSaveValues();

    if (!savePayload) {
      await alert({
        title: 'Fix the highlighted fields',
        body: 'Some fields are invalid, so nothing was saved.',
        acknowledgeLabel: 'Got it',
      });
      return;
    }

    const { values, reset } = savePayload;

    if (!hasAtLeastOneEnabledProduct(values)) {
      await alert({
        title: "Can't save configuration",
        body: 'Enable at least one plan, then save again.',
        acknowledgeLabel: 'Got it',
      });
      return;
    }

    const serializedPayload = serializeMembershipsConfigPayload({
      partnerId,
      brandId: selectedSegmentId,
      activationFlowConfigId,
      formValues: values,
    });

    try {
      const response = await saveActivationFlowConfigBundle({
        variables: {
          input: serializedPayload as SaveActivationFlowConfigBundleInput,
        },
      });

      if (!response.data?.saveActivationFlowConfigBundle?.success) {
        showErrorAlert(SAVE_FAILED_MESSAGE);
        return;
      }

      reset();
      showSuccessAlert('Membership configuration saved.');
      await onSaved?.();
    } catch (error) {
      console.error(error);
      showErrorAlert(
        error instanceof ApolloError
          ? parseMembershipsConfigError(error)
          : SAVE_FAILED_MESSAGE,
      );
    }
  }, [
    readOnly,
    selectedSegmentId,
    hasPartner,
    requestSaveValues,
    partnerId,
    selectedPartnerId,
    activationFlowConfigId,
    alert,
    saveActivationFlowConfigBundle,
    showSuccessAlert,
    onSaved,
    showErrorAlert,
  ]);

  const saveSlots = useMemo(
    () => ({
      onSaveRequestChange: setRequestSaveValues,
      onDirtyChange: setIsFormDirty,
      onSubmitStateChange: setSubmitState,
    }),
    [],
  );

  return {
    readOnly,
    liveFormValues,
    onFormValuesChange: setLiveFormValues,
    saveSlots,
    hasUnsavedChanges,
    isSaving,
    isSaveDisabled: readOnly || !submitState.isValid || isSaving,
    handleSave,
  };
};
